/**
 * CRDT Operations Monitoring Page
 * 
 * Enterprise-grade dashboard for comprehensive CRDT operations monitoring
 * with real-time state synchronization tracking and performance analytics.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import {
  VectorClockVisualization,
  // CRDTOperationHeatmap,
  // RealTimeStateSynchronization,
  // CRDTTypeSpecificMonitoring,
  // PerformanceAnalyticsDashboard,
} from '../components/crdt';
import type { CRDTState, CRDTOperation, VectorClock, CRDTType } from '../types'

// Mock data generation functions for development
const generateMockVectorClocks = (nodes: any[]) => {
  return nodes.map(node => ({
    nodeId: node.id,
    nodeName: node.name,
    vectorClock: nodes.reduce((clock, n) => {
      clock[n.id] = Math.floor(Math.random() * 100) + 1;
      return clock;
    }, {} as VectorClock),
    timestamp: new Date(Date.now() - Math.random() * 3600000),
    isActive: Math.random() > 0.2,
    status: (['online', 'offline', 'degraded'] as const)[Math.floor(Math.random() * 3)],
  }));
};

// const generateMockHeatmapData = (nodes: any[]) => {
//   const crdtTypes: CRDTType[] = ['G-Counter', 'PN-Counter', 'G-Set', 'OR-Set', 'LWW-Register', 'LWW-Element-Set'];
//   const data = [];
//   
//   for (const node of nodes) {
//     for (const type of crdtTypes) {
//       data.push({
//         nodeId: node.id,
//         crdtType: type,
//         operationType: (['merge', 'update', 'conflict', 'sync'] as const)[Math.floor(Math.random() * 4)],
//         frequency: Math.floor(Math.random() * 1000) + 10,
//         averageLatency: Math.floor(Math.random() * 500) + 50,
//         conflictRate: Math.random() * 0.3,
//         timestamp: new Date(),
//         metadata: {
//           successRate: 0.8 + Math.random() * 0.2,
//           errorCount: Math.floor(Math.random() * 10),
//           maxLatency: Math.floor(Math.random() * 2000) + 100,
//         },
//       });
//     }
//   }
//   
//   return data;
// };

// const generateMockSyncStates = (nodes: any[], crdtStates: CRDTState[]) => {
//   const syncStates = [];
//   
//   for (const node of nodes) {
//     for (const crdt of crdtStates.slice(0, 3)) { // Limit for demo
//       syncStates.push({
//         nodeId: node.id,
//         crdtId: crdt.id,
//         state: crdt.state,
//         vectorClock: nodes.reduce((clock, n) => {
//           clock[n.id] = Math.floor(Math.random() * 50) + 1;
//           return clock;
//         }, {} as VectorClock),
//         lastModified: new Date(Date.now() - Math.random() * 3600000),
//         syncStatus: (['synced', 'syncing', 'diverged', 'offline'] as const)[Math.floor(Math.random() * 4)],
//         pendingOps: Math.floor(Math.random() * 10),
//       });
//     }
//   }
//   
//   return syncStates;
// };

// const generateMockPerformanceData = () => {
//   const data = [];
//   const now = Date.now();
//   
//   for (let i = 0; i < 50; i++) {
//     const timestamp = new Date(now - (50 - i) * 60000); // Last 50 minutes
//     data.push({
//       timestamp,
//       throughput: {
//         operationsPerSecond: Math.floor(Math.random() * 500) + 100,
//         mergesPerSecond: Math.floor(Math.random() * 50) + 10,
//         conflictsPerSecond: Math.floor(Math.random() * 20) + 1,
//       },
//       latency: {
//         average: Math.floor(Math.random() * 300) + 50,
//         p50: Math.floor(Math.random() * 200) + 40,
//         p95: Math.floor(Math.random() * 800) + 200,
//         p99: Math.floor(Math.random() * 1500) + 500,
//         max: Math.floor(Math.random() * 3000) + 1000,
//       },
//       memory: {
//         totalUsage: Math.floor(Math.random() * 500 * 1024 * 1024) + 100 * 1024 * 1024,
//         perNode: {},
//         garbageCollectionEvents: Math.floor(Math.random() * 5),
//         memoryLeaks: Math.floor(Math.random() * 2),
//       },
//       network: {
//         bandwidth: Math.floor(Math.random() * 50 * 1024 * 1024) + 10 * 1024 * 1024,
//         packetsPerSecond: Math.floor(Math.random() * 10000) + 1000,
//         compressionRatio: 2 + Math.random() * 3,
//         replicationLag: Math.floor(Math.random() * 1000) + 50,
//       },
//       convergence: {
//         averageTime: Math.floor(Math.random() * 2000) + 100,
//         maxTime: Math.floor(Math.random() * 5000) + 500,
//         nodesConverged: Math.floor(Math.random() * 5) + 3,
//         totalNodes: 5,
//       },
//       errors: {
//         count: Math.floor(Math.random() * 5),
//         rate: Math.random() * 0.05,
//         types: {
//           'merge_conflict': Math.floor(Math.random() * 3),
//           'network_timeout': Math.floor(Math.random() * 2),
//           'validation_error': Math.floor(Math.random() * 1),
//         },
//       },
//     });
//   }
//   
//   return data;
// };

export const CRDTOperationsPage: React.FC = () => {
  // STATIC MOCK NODES - no live data fetching to prevent reloads
  const nodes = useMemo(() => Array.from({ length: 5 }, (_, i): any => ({
    id: `node-${i}`,
    name: `Node ${i}`,
    address: `10.0.${i + 1}.1`,
    port: 8080 + i,
    status: 'online' as const,
    role: 'follower' as const,
    region: 'us-east-1',
    zone: `zone-${i % 3 + 1}`,
    version: '1.0.0',
    uptime: 3600000,
    lastSeen: new Date(),
    metrics: {
      cpu: { usage: 0, cores: 4, load: [0, 0, 0] as [number, number, number] },
      memory: { used: 0, total: 16000000000, usage: 0 },
      network: { bytesIn: 0, bytesOut: 0, connectionsActive: 0, connectionsTotal: 0, latency: 0 },
      storage: { used: 0, total: 100000000000, usage: 0, iops: 0 },
      consensus: { term: 1, commitIndex: 0, appliedIndex: 0, role: 'follower' as const },
    },
    capabilities: ['crdt', 'consensus'],
    tags: {},
  })), []);
  const nodesLoading = false;
  // const [crdtStates, setCrdtStates] = useState<CRDTState[]>([]);
  // const [operations] = useState<CRDTOperation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  // const [autoRefresh, setAutoRefresh] = useState(false);
  // const [refreshInterval, setRefreshInterval] = useState(60); // seconds - increased from 30 to reduce aggressive refreshing

  // Generate mock data ONCE - no dependencies to prevent re-rendering loops
  const vectorClocks = useMemo(() => generateMockVectorClocks(nodes), [nodes.length]);
  // const heatmapData = useMemo(() => generateMockHeatmapData(nodes), [nodes.length]);
  // const syncStates = useMemo(() => generateMockSyncStates(nodes, crdtStates), [nodes.length, crdtStates.length]);
  // const performanceData = useMemo(() => generateMockPerformanceData(), []);
  
  // const mergeOperations = useMemo(() => {
  //   return Array.from({ length: 20 }, (_, i) => ({
  //     id: `merge-${i}`,
  //     fromNodeId: nodes[Math.floor(Math.random() * nodes.length)]?.id || 'unknown',
  //     toNodeId: nodes[Math.floor(Math.random() * nodes.length)]?.id || 'unknown',
  //     crdtId: `crdt-${Math.floor(Math.random() * 5)}`,
  //     timestamp: new Date(Date.now() - Math.random() * 300000),
  //     beforeState: {},
  //     afterState: {},
  //     duration: Math.floor(Math.random() * 500) + 50,
  //     success: Math.random() > 0.1,
  //     conflictsResolved: Math.floor(Math.random() * 5),
  //   }));
  // }, [nodes.length]);

  // const convergenceMetrics = useMemo(() => {
  //   return nodes.map(node => ({
  //     nodeId: node.id,
  //     crdtId: `crdt-${Math.floor(Math.random() * 3)}`,
  //     convergenceTime: Math.floor(Math.random() * 2000) + 100,
  //     stateSize: Math.floor(Math.random() * 1024 * 1024) + 100000,
  //     operationsSinceLastSync: Math.floor(Math.random() * 50),
  //     consistencyScore: 0.7 + Math.random() * 0.3,
  //   }));
  // }, [nodes.length]);

  // const replicationLatencies = useMemo(() => {
  //   const latencies = [];
  //   for (let i = 0; i < nodes.length; i++) {
  //     for (let j = i + 1; j < nodes.length; j++) {
  //       latencies.push({
  //         fromNode: nodes[i].id,
  //         toNode: nodes[j].id,
  //         averageLatency: Math.floor(Math.random() * 200) + 50,
  //         currentLatency: Math.floor(Math.random() * 400) + 30,
  //         maxLatency: Math.floor(Math.random() * 1000) + 200,
  //         replicationLag: Math.floor(Math.random() * 500) + 20,
  //       });
  //     }
  //   }
  //   return latencies;
  // }, [nodes.length]);

  // const crdtMetrics = useMemo(() => {
  //   return crdtStates.map(crdt => ({
  //     crdtId: crdt.id,
  //     type: crdt.type,
  //     nodeId: crdt.replicaId,
  //     metrics: {
  //       operationCount: Math.floor(Math.random() * 1000) + 100,
  //       stateSize: Math.floor(Math.random() * 1024 * 1024) + 50000,
  //       lastOperation: new Date(Date.now() - Math.random() * 3600000),
  //       conflictRate: Math.random() * 0.2,
  //       mergeLatency: Math.floor(Math.random() * 300) + 50,
  //       memoryUsage: Math.floor(Math.random() * 50 * 1024 * 1024) + 10 * 1024 * 1024,
  //       networkBandwidth: Math.floor(Math.random() * 10 * 1024 * 1024) + 1024 * 1024,
  //       garbageCollectionEvents: Math.floor(Math.random() * 10),
  //     },
  //     typeSpecific: {
  //       uniqueTags: Math.floor(Math.random() * 100) + 10,
  //       gcEvents: Math.floor(Math.random() * 5),
  //     },
  //   }));
  // }, [crdtStates.length]);

  const anomalies = useMemo(() => {
    const anomalyTypes = ['throughput', 'latency', 'memory', 'network', 'convergence'] as const;
    const severities = ['low', 'medium', 'high', 'critical'] as const;
    
    return Array.from({ length: Math.floor(Math.random() * 8) }, () => ({
      type: anomalyTypes[Math.floor(Math.random() * anomalyTypes.length)],
      severity: severities[Math.floor(Math.random() * severities.length)],
      timestamp: new Date(Date.now() - Math.random() * 300000),
      description: `Anomaly detected in ${anomalyTypes[Math.floor(Math.random() * anomalyTypes.length)]} metrics`,
      value: Math.random() * 1000,
      threshold: Math.random() * 500,
      nodeId: nodes[Math.floor(Math.random() * nodes.length)]?.id,
      suggestion: 'Review system configuration and consider scaling resources',
    }));
  }, [nodes.length]);

  // Initialize mock CRDT states ONCE
  useEffect(() => {
    // const mockStates: CRDTState[] = Array.from({ length: 6 }, (_, i) => ({
    //   id: `crdt-${i}`,
    //   type: (['G-Counter', 'PN-Counter', 'G-Set', 'OR-Set', 'LWW-Register', 'LWW-Element-Set'] as CRDTType[])[i],
    //   state: {},
    //   vectorClock: {},
    //   lastModified: new Date(Date.now() - Math.random() * 3600000),
    //   replicaId: nodes[Math.floor(Math.random() * nodes.length)]?.id || 'node-1',
    //   operations: [],
    // }));
    // setCrdtStates(mockStates);
    setIsLoading(false);
  }, []); // Empty dependency array - run only once on mount

  // NO AUTO-REFRESH - completely removed to prevent errors
  const handleRefresh = () => {
    // Manual refresh only - update timestamp without triggering re-renders
    setLastRefresh(new Date());
  };

  const handleNodeSelect = (nodeId: string) => {
    console.log('Selected node:', nodeId);
  };

  // const handleCRDTSelect = (crdtId: string) => {
  //   console.log('Selected CRDT:', crdtId);
  // };

  // const handleOptimizationApply = (suggestion: any) => {
  //   console.log('Applying optimization:', suggestion);
  // };

  // const handleAnomalyAcknowledge = (anomaly: any) => {
  //   console.log('Acknowledging anomaly:', anomaly);
  // };

  if (nodesLoading || isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-console-accent animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl suse-bold text-console-text">CRDT Operations Monitoring</h1>
          <p className="text-console-muted mt-1">
            Real-time distributed state synchronization and performance analytics
          </p>
        </div>

        <div className="flex items-center space-x-4">
          {/* Auto-refresh controls - DISABLED to prevent errors */}
          <div className="flex items-center space-x-2 opacity-50">
            <input
              type="checkbox"
              id="auto-refresh-main"
              checked={false}
              disabled
              className="rounded border-console-border text-console-accent"
            />
            <label htmlFor="auto-refresh-main" className="text-sm text-console-text">Auto-refresh (disabled)</label>
          </div>

          {/* Manual refresh */}
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-2 px-4 py-2 bg-console-accent text-white rounded hover:bg-console-accent/80 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>

          {/* Last refresh indicator */}
          <div className="text-sm text-console-muted">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Alert Summary */}
      {anomalies.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-900/20 border border-red-500/30 rounded-lg p-4"
        >
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="font-medium text-red-300">
              {anomalies.length} Active Anomal{anomalies.length === 1 ? 'y' : 'ies'} Detected
            </span>
            <span className="text-red-400 text-sm">
              ({anomalies.filter(a => a.severity === 'critical').length} critical, {anomalies.filter(a => a.severity === 'high').length} high priority)
            </span>
          </div>
        </motion.div>
      )}

      {/* Vector Clock Visualization */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <VectorClockVisualization
          nodes={nodes}
          vectorClocks={vectorClocks}
          onNodeSelect={handleNodeSelect}
          className="mb-6"
        />
      </motion.div>

      {/* CRDT Operations Heatmap - TEMPORARILY DISABLED */}
      {/* <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <CRDTOperationHeatmap
          nodes={nodes}
          heatmapData={heatmapData}
          timeRange="6h"
          onCellClick={handleCRDTSelect}
          className="mb-6"
        />
      </motion.div> */}

      {/* Real-Time State Synchronization - TEMPORARILY DISABLED DUE TO D3 ERRORS */}
      {/* <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <RealTimeStateSynchronization
          nodes={nodes}
          syncStates={syncStates}
          mergeOperations={mergeOperations}
          convergenceMetrics={convergenceMetrics}
          replicationLatencies={replicationLatencies}
          onNodeSelect={handleNodeSelect}
          onCRDTSelect={handleCRDTSelect}
          className="mb-6"
        />
      </motion.div> */}

      {/* Performance Analytics Dashboard - TEMPORARILY DISABLED */}
      {/* <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <PerformanceAnalyticsDashboard
          performanceData={performanceData}
          anomalies={anomalies}
          onAnomalyAcknowledge={handleAnomalyAcknowledge}
          className="mb-6"
        />
      </motion.div> */}

      {/* CRDT Type-Specific Monitoring - TEMPORARILY DISABLED */}
      {/* <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <CRDTTypeSpecificMonitoring
          crdtStates={crdtStates}
          operations={operations}
          metrics={crdtMetrics}
          onOptimizationApply={handleOptimizationApply}
        />
      </motion.div> */}

      {/* Temporary Notice */}
      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mt-6">
        <div className="flex items-center space-x-2">
          <AlertTriangle className="w-5 h-5 text-yellow-400" />
          <p className="text-yellow-300">
            Additional CRDT visualizations have been temporarily disabled to prevent rendering errors. 
            Only the Vector Clock Timeline is currently active.
          </p>
        </div>
      </div>
    </div>
  );
};